import { Component } from '@theme/component';

/**
 * A custom element that powers the "Bundle deal" product-page block.
 *
 * Renders a set of selectable pricing tiers (`_bundle-option` children).
 * Selecting a tier updates the `quantity` field of the associated Buy
 * Buttons form so the right amount gets added to the cart. Each tier's own
 * perks (icon/text/group blocks nested inside it) are shown/hidden purely
 * with CSS based on the `.is-selected` class, so this component only needs
 * to manage that class and the quantity field.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} optionsList
 * @extends {Component<Refs>}
 */
export class BundleDealComponent extends Component {
  requiredRefs = ['optionsList'];

  /**
   * Cached reference to the quantity input we drive. Resolved once — either
   * an existing `input[name="quantity"]` inside the target form (e.g. from a
   * Quantity block), or one we create ourselves. It must be cached because
   * a self-created input is associated to the form via the `form=""`
   * attribute rather than DOM nesting, so `form.querySelector(...)` can't
   * find it again on subsequent calls.
   * @type {HTMLInputElement | null}
   */
  #quantityInput = null;

  connectedCallback() {
    super.connectedCallback();
    this.#applySelection(this.#getSelectedOption());
  }

  /** @returns {HTMLElement[]} */
  #getOptions() {
    return Array.from(this.refs.optionsList.querySelectorAll('.bundle-option'));
  }

  /** @returns {HTMLElement | null} */
  #getSelectedOption() {
    const options = this.#getOptions();
    return options.find((option) => option.classList.contains('is-selected')) ?? options[0] ?? null;
  }

  /** @returns {HTMLFormElement | null} */
  #getForm() {
    const formId = this.dataset.formId;
    if (!formId) return this.closest('form');

    const form = document.getElementById(formId);
    return form instanceof HTMLFormElement ? form : null;
  }

  /**
   * @param {HTMLElement | null} option
   */
  #applySelection(option) {
    if (!option) return;

    for (const el of this.#getOptions()) {
      const isSelected = el === option;
      el.classList.toggle('is-selected', isSelected);
      el.setAttribute('aria-checked', isSelected ? 'true' : 'false');
    }

    this.#setQuantity(Number(option.dataset.quantity) || 1);
  }

  /**
   * @param {number} quantity
   */
  #setQuantity(quantity) {
    const input = this.#getQuantityInput();
    if (!input) return;

    input.value = quantity.toString();
    input.dispatchEvent(new Event('change', { bubbles: true }));
  }

  /** @returns {HTMLInputElement | null} */
  #getQuantityInput() {
    if (this.#quantityInput?.isConnected) return this.#quantityInput;

    const form = this.#getForm();
    if (!form) return null;

    const existing = form.querySelector('input[name="quantity"]');

    if (existing instanceof HTMLInputElement) {
      this.#quantityInput = existing;
      return existing;
    }

    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = 'quantity';
    input.setAttribute('form', form.id);
    this.appendChild(input);

    this.#quantityInput = input;
    return input;
  }

  /**
   * Handles clicking on a bundle option.
   * @param {Event} event
   */
  selectOption(event) {
    if (!(event.target instanceof Element)) return;

    const option = event.target.closest('.bundle-option');
    if (!(option instanceof HTMLElement)) return;

    this.#applySelection(option);
  }

  /**
   * Handles keyboard activation (Enter / Space) of a bundle option.
   * @param {KeyboardEvent} event
   */
  handleKeydown(event) {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    if (!(event.target instanceof Element)) return;

    const option = event.target.closest('.bundle-option');
    if (!(option instanceof HTMLElement)) return;

    event.preventDefault();
    this.#applySelection(option);
  }
}

if (!customElements.get('bundle-deal-component')) {
  customElements.define('bundle-deal-component', BundleDealComponent);
}
